import { Component, Inject, OnInit, QueryList, ViewChildren } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ITag } from 'src/app/features/tags/models/tags.models';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { BehaviorSubject, combineLatest, map, Observable, of, startWith, Subscription, tap } from 'rxjs';
import { ICategory } from 'src/app/features/categories/models/categories.models';
import { AddElementsComponent } from '../add-elements/add-elements.component';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { Actions, ofType } from '@ngrx/effects';
import { AddTagError, AddTagSuccess } from 'src/app/features/tags/store/tags.actions';
import { AddCategoryError, AddCategorySuccess } from 'src/app/features/categories/store/categories.actions';
import { AppState } from 'src/app/core/app.state';
import { Store } from '@ngrx/store';

@Component({
  selector: 'app-edit-product-organize',
  templateUrl: './edit-product-organize.component.html',
  styleUrls: ['./edit-product-organize.component.scss']
})
export class EditProductOrganizeComponent implements OnInit {
  @ViewChildren(MatAutocompleteTrigger) autocompleteTriggers!: QueryList<MatAutocompleteTrigger>;
  private subscriptions: Subscription[] = [];
  form!: FormGroup
  tags: ITag[] = [];
  readonly separatorKeysCodes = [ENTER, COMMA] as const;
  tags$: Observable<ITag[]> = of([]);
  filteredTags$!: Observable<ITag[]>;
  categories$: Observable<ICategory[]> = of([]);
  filteredCategories$!: Observable<ICategory[]>;
  categories!: Array<ICategory>
  categoriesSelected$ = new BehaviorSubject<ICategory[]>([]);
  tagsSelected$ = new BehaviorSubject<ITag[]>([]);
  constructor(
    public dialogRef: MatDialogRef<EditProductOrganizeComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: FormBuilder,
    private dialog: MatDialog,
    private actions$: Actions,
    private store: Store<AppState>
  ) {
    this.tags$ = this.store.select((state) => [...state.tags.allTags].sort((a, b) => a.name.localeCompare(b.name)));
    this.categories$ = this.store.select((state) => [...state.categories.allCategories].sort((a, b) => a.name.localeCompare(b.name)))
  }

  ngOnInit(): void {
    this.form = this.fb.group({
      categoryControl: [this.data?.categoryControl ?? []],
      tagControl: [this.data?.tagControl ?? []],
    });
    this.categoriesSelected$.next(this.data?.categories);
    this.tagsSelected$.next(this.data?.tags)

    this.filteredTags$ = combineLatest([
      this.tags$,
      this.tagsSelected$,
      this.form.get('tagControl')!.valueChanges.pipe(startWith(''))
    ]).pipe(
      map(([allTags, selected, search]) => {
        const searchLower = search?.toLowerCase() || '';
        return allTags.filter(cat =>
          cat.name.toLowerCase().includes(searchLower) &&
          !selected.some(sel => sel.id === cat.id)
        );
      })
    );

    this.filteredCategories$ = combineLatest([
      this.categories$,
      this.categoriesSelected$,
      this.form.get('categoryControl')!.valueChanges.pipe(startWith(''))
    ]).pipe(
      map(([allCategories, selected, search]) => {
        const searchLower = search?.toLowerCase() || '';
        return allCategories.filter(cat =>
          cat.name.toLowerCase().includes(searchLower) &&
          !selected.some(sel => sel.id === cat.id)
        );
      })
    );



    const sub1 = this.actions$.pipe(
      ofType(AddTagSuccess),
      tap((action) => {
        this.form.get('tagControl')?.setValue('');
        this.tags = [...this.tags, action.tag]
      })
    ).subscribe();

    const sub2 = this.actions$.pipe(
      ofType(AddTagError),
      tap(() => {
        this.form.get('tagControl')?.setValue('');
      })
    ).subscribe();

    const sub3 = this.actions$.pipe(
      ofType(AddCategorySuccess),
      tap((action) => {
        this.form.get('categoryControl')?.setValue('');
        this.categories = [...this.categories, ...action.categories]
      })
    ).subscribe();

    const sub4 = this.actions$.pipe(
      ofType(AddCategoryError),
      tap(() => {
        this.form.get('categoryControl')?.setValue('');
      })
    ).subscribe();


    this.subscriptions.push(sub1, sub2, sub3, sub4);
  }

  onTagSelected(tag: ITag) {
    if (tag) {
      const current = this.tagsSelected$.getValue();
      if (!current.some(c => c.id === tag.id)) {
        this.tagsSelected$.next([...current, tag]);
      }
    }
    this.form.get('tagControl')?.setValue('');
  }

  onCategorySelected(category: ICategory) {
    if (category) {
      const current = this.categoriesSelected$.getValue();
      if (!current.some(c => c.id === category.id)) {
        this.categoriesSelected$.next([...current, category]);
      }
    }
    this.form.get('categoryControl')?.setValue('');
  }

  addNewElement(newElementName: string, type: string, index?: number) {

    const dialogRef = this.dialog.open(AddElementsComponent, {
      data: {
        name: newElementName,
        type: type,
        index: index
      },
      disableClose: true,
    });
    dialogRef.afterClosed().subscribe(() => {
      this.autocompleteTriggers.forEach(trigger => trigger.closePanel());
    });
  }

  cancelTag(id: number) {
    const current = this.tagsSelected$.getValue();
    const updated = current.filter(tag => tag.id !== id);
    this.tagsSelected$.next(updated);
  }

  cancelCategory(id: number) {
    const current = this.categoriesSelected$.getValue();
    const updated = current.filter(cat => cat.id !== id);
    this.categoriesSelected$.next(updated);
  }

  save(): boolean | void {
    if(this.form.invalid){
      this.form.markAllAsTouched()
      return false
    }
    this.dialogRef.close({
      tags: this.tagsSelected$.getValue(),
      categoryControl: this.form.get('categoryControl')?.value,
      tagControl: this.form.get('tagControl')?.value,
      categories: this.categoriesSelected$.getValue(),
    })
  }

  closed(){
    this.dialogRef.close()
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub => sub.unsubscribe()))
  }
}
